import React from 'react';
import {
  BarChart3,
  Smartphone,
  Shield,
  CreditCard,
  Zap,
  MessageCircle,
  Check,
  DollarSign,
  HeartHandshake,
  Users
} from 'lucide-react';
import Navbar from './Navbar';
import Footer from './Footer';

const Features = () => {
  const mainFeatures = [
    { icon: DollarSign, title: 'Unbeatable Rates', desc: 'Our rates are the most economical and competitive in the industry, ensuring maximum value for your money.', bg: 'bg-brand-light', color: 'text-brand' },
    { icon: BarChart3, title: 'Real-Time Connectivity', desc: 'Personalized dedicated Internet Server with lease-line connectivity for real-time parcel tracking.', bg: 'bg-brand-light', color: 'text-brand' },
    { icon: CreditCard, title: 'Email Invoicing', desc: 'Convenient monthly billing by email for contract parties with detailed transaction reports.', bg: 'bg-brand-light', color: 'text-brand' },
    { icon: Zap, title: 'Bulk Delivery', desc: 'Bulk quantity arrangements from anywhere in India for efficient bulk delivery across the country.', bg: 'bg-brand-light', color: 'text-brand' },
  ];

  const techFeatures = [
    { icon: BarChart3, title: 'Advanced Tracking System', desc: 'Real-time GPS tracking with detailed status updates and location information.' },
    { icon: Smartphone, title: 'Mobile App Integration', desc: 'User-friendly mobile application for booking, tracking, and managing shipments.' },
    { icon: Shield, title: 'Secure Payment Gateway', desc: 'Multiple secure payment options with encrypted transactions and instant confirmations.' },
    { icon: CreditCard, title: 'Automated Billing', desc: 'Automated invoice generation and billing system with detailed cost breakdowns.' },
    { icon: Zap, title: 'Route Optimization', desc: 'AI-powered route optimization for faster delivery times and reduced costs.' },
    { icon: MessageCircle, title: '24/7 Customer Support', desc: 'Round-the-clock customer support through multiple channels including chat, phone, and email.' },
  ];

  const serviceQuality = [
    { title: '99.5% On-Time Delivery', desc: 'Industry-leading delivery performance with minimal delays' },
    { title: 'Zero Damage Policy', desc: 'Careful handling ensures your packages arrive in perfect condition' },
    { title: 'Professional Staff', desc: 'Trained and experienced team members at every level' },
    { title: 'Quality Assurance', desc: 'Regular quality checks and continuous improvement processes' },
  ];

  const customerBenefits = [
    { title: 'Cost Savings', desc: 'Competitive pricing with no hidden charges' },
    { title: 'Time Efficiency', desc: 'Faster processing and delivery times' },
    { title: 'Peace of Mind', desc: 'Insurance coverage and reliable tracking' },
    { title: 'Personalized Service', desc: 'Dedicated account managers for business clients' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      <Navbar />

      <section className="bg-gradient-to-r from-brand to-brand-hover text-white pt-28 pb-16 md:pt-32 md:pb-20">
        <div className="container mx-auto px-4 lg:px-6 text-center">
          <p className="text-brand-light text-sm font-medium uppercase tracking-widest mb-3">
            Our Features
          </p>
          <h1 className="text-3xl md:text-5xl font-semibold mb-4">Advanced Technology & Superior Service</h1>
          <p className="text-lg text-blue-100 max-w-2xl mx-auto">
            Cutting-edge technology and industry expertise for reliable, efficient logistics solutions.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 lg:px-6">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-semibold text-dark mb-4">Why Choose Metro Logistics</h2>
            <p className="text-secondary max-w-3xl mx-auto">
              We leverage cutting-edge technology and industry expertise to provide you with the most reliable and efficient logistics solutions.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {mainFeatures.map((feature) => {
              const Icon = feature.icon;
              return (
                <div key={feature.title} className="text-center bg-white rounded-card shadow-card p-6 border border-gray-100 hover:shadow-soft transition-shadow">
                  <div className={`${feature.bg} w-14 h-14 rounded-card flex items-center justify-center mx-auto mb-4`}>
                    <Icon className={`w-7 h-7 ${feature.color}`} />
                  </div>
                  <h3 className="text-lg font-semibold text-dark mb-2">{feature.title}</h3>
                  <p className="text-secondary text-sm">{feature.desc}</p>
                </div>
              );
            })}
          </div>

          <div className="bg-white rounded-card shadow-card border border-gray-100 p-6 md:p-10 mb-16">
            <h2 className="text-2xl font-semibold text-dark mb-8 text-center">Technology Features</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {techFeatures.map((feature) => {
                const Icon = feature.icon;
                return (
                  <div key={feature.title} className="bg-gray-50 rounded-card p-5 border border-gray-100">
                    <div className="flex items-center mb-3 gap-3">
                      <div className="bg-brand-light w-10 h-10 rounded-btn flex items-center justify-center shrink-0">
                        <Icon className="w-5 h-5 text-brand" />
                      </div>
                      <h4 className="text-base font-semibold text-dark">{feature.title}</h4>
                    </div>
                    <p className="text-secondary text-sm">{feature.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-card shadow-card p-6 md:p-8 border border-gray-100">
              <h3 className="text-xl font-semibold text-dark mb-6 flex items-center gap-2">
                <Users className="w-5 h-5 text-brand" />
                Service Quality
              </h3>
              <ul className="space-y-4">
                {serviceQuality.map((item) => (
                  <li key={item.title} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-success shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-dark text-sm">{item.title}</h4>
                      <p className="text-secondary text-sm">{item.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white rounded-card shadow-card p-6 md:p-8 border border-gray-100">
              <h3 className="text-xl font-semibold text-dark mb-6 flex items-center gap-2">
                <HeartHandshake className="w-5 h-5 text-brand" />
                Customer Benefits
              </h3>
              <ul className="space-y-4">
                {customerBenefits.map((item) => (
                  <li key={item.title} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-brand shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-dark text-sm">{item.title}</h4>
                      <p className="text-secondary text-sm">{item.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Features;
